import { useEffect, useRef, useState } from 'react'
import { Upload, FileText, X, Loader2 } from 'lucide-react'
import { Button } from '@playbooks/ui/components/button'
import { ActionCard, Section } from '@playbooks/core/src/interfaces/ActionCard'
import { FileItem, ViewMode } from '@playbooks/action-cards-utils'
import { cn } from '@playbooks/ui/lib/utils'

import { InputProps } from '../types'
import { useAppContext } from '../../../contexts/AppContext'
import { useTranslation } from '../../../contexts/localization/Localization'

interface TrackedFile extends FileItem {
  loading: boolean
  file?: File
}

export default function FileUpload({ question, onChange }: InputProps<FileItem[]>) {
  const { text } = useTranslation()
  const [trackedFiles, setTrackedFiles] = useState<TrackedFile[]>([])
  const [showLoader, setShowLoader] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const fieldRef = useRef<HTMLDivElement>(null)
  const {
    data,
    config,
    settings: { viewMode, accentColor },
  } = useAppContext()
  const isDocumentView = viewMode === ViewMode.DOC

  const fileUploadService = config.fileUploadServiceProvider?.getService(question)

  useEffect(() => {
    // Initialize trackedFiles from question.value on mount
    if (question.value) {
      const initialFiles = (question.value as FileItem[]).map(
        (file): TrackedFile => ({
          id: file.id,
          name: file.name,
          loading: false,
          url: file.url,
        }),
      )
      Promise.resolve().then(() => setTrackedFiles(initialFiles))
    } else {
      Promise.resolve().then(() => setTrackedFiles([]))
    }

    // We need to use array dependency and setState in use effect
    // without this the file field sometimes flickering if we use
    // the key prop on FileUpload component to force update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(question.value)])

  const uploadFiles = async (files: TrackedFile[]) => {
    const separatorScope = fieldRef.current?.closest('[data-separator-scope]')?.getAttribute('data-separator-scope')
    const sectionsScope = fieldRef.current?.closest('[data-section-id]')?.getAttribute('data-section-id')

    const sectionData = getHeaderData({
      actionCards: data,
      separatorId: separatorScope ? parseInt(separatorScope) : -1,
      sectionId: sectionsScope ? parseInt(sectionsScope) : -1,
    })

    if (!sectionData) {
      return console.error('[FileUpload]: Cannot upload files without a sectionData')
    }

    if (!fileUploadService) {
      console.error('[FileUpload]: No file upload service provided')
      return
    }

    setShowLoader(true)

    const uploadingFiles = files.flatMap(f => f.file || [])

    const result = await fileUploadService.uploadFiles(uploadingFiles, sectionData)

    setTrackedFiles(prev => {
      const successfulUploads = result.filter(r => !r.error)
      const erroredUploads = result.filter(r => r.error)

      return [
        ...prev.filter(p => !files.some(f => f.name === p.name)),
        ...successfulUploads.map(s => ({ ...s, loading: false })),
        ...erroredUploads.map(e => ({ id: Math.random(), name: e.name, error: e.error, loading: false })),
      ] as TrackedFile[]
    })

    const successfulUploads = result
      .filter(fileItem => !fileItem.error)
      .map((r, i) => ({
        id: r.id,
        name: files[i].name,
        url: r.url!,
      }))

    if (successfulUploads.length > 0) {
      onChange([...(question.value ? (question.value as FileItem[]) : []), ...successfulUploads])
    }

    setShowLoader(false)
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).map<TrackedFile>(file => ({
        id: Math.random(),
        name: file.name,
        file,
        loading: true,
      }))

      setTrackedFiles(prev => [...prev, ...newFiles])
      uploadFiles(newFiles)
    }

    // Reset so the same file can be re-added if needed
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  async function removeFile(index: number) {
    setShowLoader(true)

    const newFileItems = [...trackedFiles]
    const fileItem = newFileItems.splice(index, 1)[0]

    try {
      await fileUploadService?.deleteFile(fileItem)

      setTrackedFiles(prev => prev.filter((_, i) => i !== index))
      onChange(question.value ? (question.value as FileItem[]).filter((_, i) => i !== index) : [])
    } catch (error) {
      console.error('[removeFile]:', error)
    }

    setShowLoader(false)
  }

  return (
    <div ref={fieldRef} className="w-full">
      {/* Upload trigger row */}
      <div
        className={cn(
          'flex items-center rounded-md border border-input overflow-hidden',
          isDocumentView && 'rounded-none',
        )}
      >
        <Button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={showLoader || fileUploadService?.isDisabled}
          className={cn('rounded-none shrink-0 border-none', accentColor && 'bg-(--color-ac-accent) text-white')}
        >
          <Upload className="size-4" />
          {text('uploadFile')}
        </Button>
        <span className="flex-1 truncate px-3 text-sm text-muted-foreground">
          {trackedFiles.length === 0
            ? text('noFilesUploaded')
            : `${trackedFiles.length} file${trackedFiles.length > 1 ? 's' : ''} selected`}
        </span>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="sr-only"
          onChange={handleFileChange}
          disabled={showLoader}
        />
      </div>

      {/* File list */}
      {trackedFiles.length > 0 && (
        <ul className="mt-3">
          {trackedFiles.map((tf, index) => (
            <li
              key={`${tf.name}-${index}`}
              className="flex items-center gap-3 border-b border-border py-3 last:border-b-0 cursor-pointer"
              onClick={() => {
                if (tf.url) {
                  window.open(tf.url, '_blank')
                }
              }}
            >
              {tf.loading ? (
                <Loader2 className="size-5 shrink-0 text-muted-foreground animate-spin" />
              ) : (
                <FileText className={`size-5 shrink-0 ${tf.error ? 'text-destructive' : 'text-foreground'}`} />
              )}
              <span
                className={`flex-1 truncate text-sm ${tf.loading ? 'text-muted-foreground' : tf.error ? 'text-destructive' : 'text-foreground'}`}
              >
                {tf.name}
              </span>
              {tf.error ? (
                <span className="shrink-0 text-xs text-destructive flex items-center gap-2">
                  {tf.error}
                  <RemoveFileButton removeFile={removeFile} index={index} tf={tf} />
                </span>
              ) : (
                <RemoveFileButton removeFile={removeFile} index={index} tf={tf} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const getHeaderData = (props: { actionCards: ActionCard; separatorId: number; sectionId: number }): Section => {
  const { actionCards, separatorId, sectionId } = props
  // get a separator the current section belongs to
  const separator = actionCards.sections.find(sec => sec.id === separatorId)
  const section = actionCards.sections.find(sec => sec.id === sectionId)

  if (separator) {
    return separator
  }

  return section as Section
}
function RemoveFileButton({
  removeFile,
  index,
  tf,
}: {
  removeFile: (index: number) => void
  index: number
  tf: TrackedFile
}) {
  return (
    <button
      type="button"
      onClick={e => {
        e.stopPropagation()
        removeFile(index)
      }}
      className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
      aria-label={`Remove ${tf.name}`}
    >
      <X className="size-4" />
    </button>
  )
}
